import { Ban, CirclePause, Fingerprint, KeyRound, Link2, Play, ShieldAlert, ShieldCheck, ShieldX, Snowflake, SlidersHorizontal } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { AuditVerifyResult, NodeTrustInfo, TrustState } from '@orgus/protocol';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  Panel,
  RelativeTime,
  RowStatus,
  Segmented,
  Select,
  SkeletonRows,
  StatusDot,
  Tooltip,
  cn,
  toUiError,
  toast,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { capabilityLabel } from './ceilingHint.ts';
import { NodeAttention, RemoteMachines } from './NodeAttention.tsx';
import { RelayAccess } from './RelayAccess.tsx';
import { pausable, useTrust } from './useTrust.ts';
import type { TrustApi } from './useTrust.ts';


/** SHA256:abcd...wxyz, short enough for a table cell; the full value is in the tooltip. */
const shortFp = (f: string) => (f.length > 22 ? `${f.slice(0, 15)}...${f.slice(-6)}` : f);

export default function TrustSettings() {
  const t = useTrust();
  if (t.loading && !t.state) {
    return (
      <div className="flex flex-col gap-4" role="status" aria-busy="true" aria-label="Loading trust settings">
        <SkeletonRows rows={3} />
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (!t.state) return <ErrorState error={t.error ?? new Error('The hub sent no trust state.')} onRetry={t.refresh} />;
  return (
    <div className="flex flex-col gap-4">
      {t.error != null && <ErrorState variant="inline" error={t.error} onRetry={t.refresh} />}
      {t.state.commandKey.error && (
        <ErrorState
          variant="inline"
          error={{
            what: 'Commands to machines are refused: the command signing key did not load.',
            why: t.state.commandKey.error,
            next: 'Set COMMAND_SIGNING_KEY to a key made with pnpm --filter @orgus/control-plane keys, or remove it to use a generated key, then redeploy.',
          }}
        />
      )}
      <NodeAttention t={t} s={t.state} />
      <KillSwitches t={t} s={t.state} />
      <RelayAccess t={t} s={t.state} />
      <RemoteMachines t={t} s={t.state} />
      <MachineIdentity s={t.state} />
      <WriteRules t={t} s={t.state} />
      <AuditIntegrity t={t} s={t.state} />
      <Refusals s={t.state} />
      <p className="text-xs text-fg-subtle">
        Hub command key <span className="font-mono">{t.state.commandKey.fingerprint || 'not loaded'}</span>. Every command to a machine is signed with it and checked there.
        {t.state.commandKey.keystore && ` Kept in ${t.state.commandKey.keystore}.`}
        {t.state.commandKey.warning && ` ${t.state.commandKey.warning}`}
        {t.fetchedAt && (
          <>
            {' '}
            Updated <RelativeTime ts={t.fetchedAt} />.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * Freeze all writes and pause projects, each behind a confirmation naming what happens. Also on
 * the Security page, where `children` adds the revoke row to the same panel.
 */
export function KillSwitches({ t, s, title = 'Kill switches', children }: { t: TrustApi; s: TrustState; title?: string; children?: ReactNode }) {
  const projects = useOrgus((st) => st.projects);
  const [confirm, setConfirm] = useState<null | 'freeze' | 'unfreeze' | { pause: string } | { resume: string }>(null);
  const [reason, setReason] = useState('');
  const [pick, setPick] = useState('');
  const candidates = pausable(projects, s);
  const nameOf = (id: string) => projects[id]?.name ?? id;
  const open = (c: typeof confirm) => {
    setReason('');
    setConfirm(c);
  };
  const close = (o: boolean) => !o && setConfirm(null);

  return (
    <Panel title={title} sub="Stop always works" flush>
      <div className="flex flex-col divide-y divide-border-subtle">
        <div className="flex flex-col gap-3 px-row-x py-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-ui font-medium text-fg">
              {s.frozen ? <RowStatus tone="danger" icon="stopped" label="Frozen" /> : 'Freeze all writes'}
            </p>
            <p className="text-caption text-fg-muted">
              {s.frozen ? (
                <>
                  Every action that starts or changes work is refused. By {s.frozen.by} <RelativeTime ts={s.frozen.at} />
                  {s.frozen.reason ? `: ${s.frozen.reason}` : ''}. Reads keep working.
                </>
              ) : (
                'Refuses every new session, command and change on every machine at once.'
              )}
            </p>
          </div>
          {s.frozen ? (
            // On a phone the actions keep their own width under the text, never a full-width bar.
            <Button size="sm" variant="secondary" icon={<Play />} className="self-start md:self-auto" onClick={() => open('unfreeze')}>
              Unfreeze writes
            </Button>
          ) : (
            <Button size="sm" variant="danger" icon={<Snowflake />} className="self-start md:self-auto" onClick={() => open('freeze')}>
              Freeze all writes
            </Button>
          )}
        </div>

        {s.pausedProjects.map((p) => (
          <div key={p.projectId} data-row="" className="flex min-h-row-2line items-center gap-3 px-row-x">
            <RowStatus tone="attention" icon="paused" label="Paused" hideLabel="below-lg" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-ui text-fg">{nameOf(p.projectId)}</span>
              <span data-meta="" className="truncate text-caption text-fg-subtle">
                By {p.by} <RelativeTime ts={p.at} />
                {p.reason ? `: ${p.reason}` : ''}
              </span>
            </span>
            <Button size="sm" variant="ghost" icon={<Play />} onClick={() => open({ resume: p.projectId })}>
              Resume project
            </Button>
          </div>
        ))}
        <div className="flex flex-col gap-3 px-row-x py-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p className="text-ui font-medium text-fg">Pause a project</p>
            <p className="text-caption text-fg-muted">Refuses new work in one project and leaves every other project running.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select
              size="sm"
              aria-label="Project to pause"
              wrapperClassName="w-48"
              placeholder={candidates.length ? 'Choose a project' : 'No other projects'}
              options={candidates.map((p) => ({ value: p.id, label: p.name }))}
              value={pick}
              onValueChange={setPick}
              disabled={!candidates.length}
            />
            <Button size="sm" variant="ghost" icon={<CirclePause />} disabled={!pick} onClick={() => open({ pause: pick })}>
              Pause
            </Button>
          </div>
        </div>
        {children}
      </div>

      <ConfirmDialog
        open={confirm === 'freeze'}
        onOpenChange={close}
        title="Freeze all writes?"
        description="Every machine refuses new sessions, commands, worktree removals and project changes until an admin unfreezes. Stopping work still runs."
        confirmLabel="Freeze all writes"
        onConfirm={() => t.freeze(reason.trim())}
      >
        <Input label="Reason" placeholder="For example: investigating a leaked token" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} autoFocus />
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'unfreeze'}
        onOpenChange={close}
        title="Unfreeze Orgus?"
        description="Write actions are allowed again on every machine, within each project's pause and each machine's own ceiling."
        confirmLabel="Unfreeze writes"
        tone="primary"
        onConfirm={() => t.unfreeze()}
      />
      <ConfirmDialog
        open={typeof confirm === 'object' && confirm !== null && 'pause' in confirm}
        onOpenChange={close}
        title={`Pause ${confirm && typeof confirm === 'object' && 'pause' in confirm ? nameOf(confirm.pause) : ''}?`}
        description="New sessions, commands and changes in this project are refused on every machine until an admin resumes it."
        confirmLabel="Pause project"
        onConfirm={async () => {
          if (confirm && typeof confirm === 'object' && 'pause' in confirm) await t.pause(confirm.pause, reason.trim());
          setPick('');
        }}
      >
        <Input label="Reason" placeholder="For example: a bad deploy is being rolled back" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} autoFocus />
      </ConfirmDialog>
      <ConfirmDialog
        open={typeof confirm === 'object' && confirm !== null && 'resume' in confirm}
        onOpenChange={close}
        title={`Resume ${confirm && typeof confirm === 'object' && 'resume' in confirm ? nameOf(confirm.resume) : ''}?`}
        description="Write actions in this project are allowed again."
        confirmLabel="Resume project"
        tone="primary"
        onConfirm={async () => {
          if (confirm && typeof confirm === 'object' && 'resume' in confirm) await t.resume(confirm.resume);
        }}
      />
    </Panel>
  );
}

function IdentityBadge({ n }: { n: NodeTrustInfo }) {
  if (n.status === 'key') {
    return (
      <Badge tone="passed" icon={<ShieldCheck />}>
        Key verified
      </Badge>
    );
  }
  return (
    <Tooltip content="Enrolled before machine keys. It is upgraded to a key the next time an updated agent connects.">
      <Badge tone="waiting" icon={<ShieldAlert />}>
        Secret only
      </Badge>
    </Tooltip>
  );
}

/** Machine identity rows hold two lines: cells sit at the top, with room above and below. */
const TOP = 'whitespace-normal py-3 align-top';

function MachineIdentity({ s }: { s: TrustState }) {
  const machines = useOrgus((st) => st.machines);
  const rows = [...s.nodes].sort((a, b) => (machines[a.machineId]?.name ?? '').localeCompare(machines[b.machineId]?.name ?? ''));
  const legacy = rows.filter((n) => n.status === 'legacy').length;
  return (
    <Panel title="Machine identity" icon={<Fingerprint />} sub={rows.length ? `${rows.length - legacy} of ${rows.length} with a verified key` : undefined} flush>
      {rows.length === 0 ? (
        <EmptyState icon={<KeyRound />} title="No machines yet" description="Each machine makes its own key when it first connects. Add one in Machines." />
      ) : (
        <Table aria-label="Machine identity">
          <TableHeader>
            <TableRow>
              <TableHead>Machine</TableHead>
              <TableHead>Identity</TableHead>
              <TableHead>Key</TableHead>
              <TableHead>Its own ceiling</TableHead>
              <TableHead>Agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((n) => {
              const m = machines[n.machineId];
              return (
                <TableRow key={n.machineId}>
                  <TableCell primary className={TOP}>
                    <div className="flex items-center gap-2">
                      <StatusDot tone={m?.online ? 'passed' : 'offline'} label={m?.online ? 'Online' : 'Offline'} />
                      <span className="truncate">{m?.name ?? n.machineId}</span>
                    </div>
                  </TableCell>
                  <TableCell className={TOP}>
                    <IdentityBadge n={n} />
                  </TableCell>
                  <TableCell className={TOP}>
                    {n.fingerprint ? (
                      <div className="flex flex-col gap-1">
                        <Tooltip content={n.fingerprint}>
                          <span className="font-mono text-caption text-fg-muted">{shortFp(n.fingerprint)}</span>
                        </Tooltip>
                        <div className="flex items-center gap-2 text-caption text-fg-subtle">
                          {n.storage === 'file' ? (
                            <Tooltip content="Kept in agent.json, readable only by its owner. An OS keystore would keep it off disk; not supported yet.">
                              <Badge tone="waiting" variant="outline" icon={<KeyRound />}>
                                File key
                              </Badge>
                            </Tooltip>
                          ) : (
                            <Badge tone="passed" variant="outline" icon={<KeyRound />}>
                              OS keystore
                            </Badge>
                          )}
                          <span>
                            Verified <RelativeTime ts={n.lastVerifiedAt} />
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-fg-subtle">None yet</span>
                    )}
                  </TableCell>
                  <TableCell className={TOP}>
                    {n.ceilings === null ? (
                      <span className="text-fg-subtle">Not reported</span>
                    ) : n.ceilings.length === 0 ? (
                      <span className="text-fg-subtle">None set</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {n.ceilings.map((c) => (
                          <Badge key={c} tone="neutral" variant="outline" icon={<Ban />}>
                            {capabilityLabel(c)}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className={TOP}>
                    <div className="flex flex-col items-start gap-1">
                      <span className="font-mono text-caption text-fg-muted">{m?.agentVersion || 'Unknown'}</span>
                      {n.belowMinimum && (
                        <Badge tone="failed" icon={<ShieldX />}>
                          Below minimum
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <p className="border-t border-border-subtle px-4 py-3 text-caption text-fg-subtle">
        A machine&apos;s ceiling is set on that machine with <span className="font-mono">orgus agent ceiling</span>. The console cannot raise it.
      </p>
    </Panel>
  );
}

function WriteRules({ t, s }: { t: TrustApi; s: TrustState }) {
  const [version, setVersion] = useState(s.minAgentVersion ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A save the hub refused, said inline in the panel: errors never go in a toast.
  const [saveError, setSaveError] = useState<unknown>(null);
  const dirty = version.trim() !== (s.minAgentVersion ?? '');
  const save = async () => {
    const v = version.trim();
    if (v && !/^v?\d+(\.\d+){0,2}$/.test(v)) {
      setError('Use a version such as 0.2.0, or leave it empty for no minimum.');
      return;
    }
    setError(null);
    setSaveError(null);
    setSaving(true);
    try {
      await t.saveSettings({ minAgentVersion: v || null });
      toast(v ? `Minimum agent version is now ${v.replace(/^v/i, '')}` : 'No minimum agent version', { tone: 'success' });
    } catch (e) {
      setSaveError({ ...toUiError(e), what: 'The minimum agent version was not saved.' });
    } finally {
      setSaving(false);
    }
  };
  const setKeys = (v: 'optional' | 'required') => {
    setSaveError(null);
    return t.saveSettings({ requireKeys: v === 'required' }).then(
      () => toast(v === 'required' ? 'Machine keys are now required for write actions' : 'Machine keys are optional for write actions', { tone: 'success' }),
      (e: unknown) => setSaveError({ ...toUiError(e), what: 'The machine key rule was not changed.' }),
    );
  };
  return (
    <Panel title="Write rules" icon={<SlidersHorizontal />}>
      <div className="grid gap-6 sm:grid-cols-2">
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label htmlFor="trust-min-version" className="text-sm font-medium text-fg">
            Minimum agent version
          </label>
          <div className="flex gap-2">
            <Input
              id="trust-min-version"
              aria-describedby="trust-min-version-hint"
              aria-invalid={!!error}
              placeholder="No minimum"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              wrapperClassName="min-w-0 flex-1"
              className="font-mono"
            />
            <Button type="submit" variant="outline" loading={saving} disabled={!dirty}>
              Save
            </Button>
          </div>
          <span id="trust-min-version-hint" className={cn('text-xs', error ? 'text-failed' : 'text-fg-subtle')}>
            {error ?? 'Older agents keep reporting but get no write commands.'}
          </span>
        </form>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg">Machine keys for write actions</span>
          <Segmented
            label="Machine keys for write actions"
            value={s.requireKeys ? 'required' : 'optional'}
            onChange={setKeys}
            options={[
              { value: 'optional', label: 'Optional' },
              { value: 'required', label: 'Required' },
            ]}
          />
          <span className="text-xs text-fg-subtle">Required refuses write actions to machines that only have a secret.</span>
        </div>
      </div>
      {saveError != null && <ErrorState variant="inline" error={saveError} className="mt-4" />}
    </Panel>
  );
}

function AuditIntegrity({ t, s }: { t: TrustApi; s: TrustState }) {
  const [result, setResult] = useState<(AuditVerifyResult & { checkedAt: number }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      setResult({ ...(await t.verifyAudit()), checkedAt: Date.now() });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      title="Audit log integrity"
      icon={<Link2 />}
      sub={`${s.audit.chained} chained ${s.audit.chained === 1 ? 'entry' : 'entries'}`}
      actions={
        <Button size="sm" variant="ghost" loading={busy} onClick={verify}>
          Verify now
        </Button>
      }
    >
      {error != null ? (
        <ErrorState variant="inline" error={error} onRetry={verify} />
      ) : result ? (
        <div className={cn('flex items-start gap-3 rounded-md border px-3 py-2', result.ok ? 'border-passed bg-passed-soft' : 'border-failed bg-failed-soft')}>
          {result.ok ? <ShieldCheck className="mt-0.5 size-4 text-passed" /> : <ShieldX className="mt-0.5 size-4 text-failed" />}
          <div className="min-w-0 text-sm">
            {result.ok ? (
              <>
                <div className="font-medium text-fg">Intact</div>
                <div className="text-fg-muted">
                  {result.chained} chained {result.chained === 1 ? 'entry' : 'entries'} link back unbroken
                  {result.legacy ? `; ${result.legacy} written before chaining are not covered` : ''}. Checked <RelativeTime ts={result.checkedAt} />.
                </div>
              </>
            ) : (
              <>
                <div className="font-medium text-fg">Broken at line {result.brokenAt?.line}</div>
                <div className="text-fg-muted">{result.brokenAt?.reason}</div>
                <div className="text-fg-muted">Entries before that line verify. Keep a copy of audit.jsonl from the hub folder before anything else writes to it.</div>
              </>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-fg-muted">
          Every audit entry carries the hash of the one before it, so a changed, removed or reordered entry shows up here. Also from a terminal: <span className="font-mono">orgus audit verify</span>.
        </p>
      )}
      {s.audit.head && (
        <p className="mt-3 text-xs text-fg-subtle">
          Newest hash <span className="font-mono">{s.audit.head.slice(0, 16)}</span>
        </p>
      )}
    </Panel>
  );
}

function Refusals({ s }: { s: TrustState }) {
  const machines = useOrgus((st) => st.machines);
  return (
    <Panel title="Refused connections and commands" icon={<ShieldX />} sub={s.rejections.length ? `${s.rejections.length} recent` : undefined} flush>
      {s.rejections.length === 0 ? (
        <EmptyState icon={<ShieldCheck />} title="Nothing refused" description="Connections that cannot prove a machine key, and commands a machine drops as forged, replayed or expired, appear here." />
      ) : (
        <ul className="divide-y divide-border-subtle">
          {s.rejections.map((r, i) => (
            <li key={`${r.at}-${i}`} className="flex items-start gap-3 px-4 py-3">
              <ShieldX className="mt-0.5 size-4 shrink-0 text-failed" aria-hidden />
              <div className="min-w-0 flex-1 text-sm">
                <div className="text-fg">
                  {r.what}
                  {r.machineId ? <span className="text-fg-muted"> on {machines[r.machineId]?.name ?? r.machineId}</span> : null}
                </div>
                <div className="text-fg-muted">{r.reason}</div>
              </div>
              <RelativeTime ts={r.at} className="shrink-0 text-xs text-fg-subtle" />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
